/* eslint-disable max-lines */
import React, {useCallback, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import Resumable from 'resumablejs'

const UploadFile = (props) => {

  const {
    id,
    name,
    label,
    value,
    error,
    onChange,
    onSetFile,
    onStateChange,
    onSetFileDelete,
    onSetFileSuccess,
    onError,
    config,
    wording,
  } = props

  const [progress, setProgress] = useState(0)
  const [file, setFile] = useState(value)
  const [fileName, setFileName] = useState(null)
  const browseNode = useRef(null)
  const deleteNode = useRef(null)

  const updateValue = useCallback(
    (f) => {
      let id = f ? f.id : null
      let path = f.path || file.path

      onChange({
        name,
        value: {
          id,
          path,
        }
      })
    },
    [file, name, onChange]
  )

  const handleSetFile = (file) => {
    setFile(file)
    onSetFile(file)
  }

  useEffect(() => {
    setFile(value)
  }, [value])

  useEffect(() => {
    let mounted = true
    const setError = (type, data) => onError({type, data})

    const minFileSizeErrorCallback = (f) => {
      setError(UploadFile.errorTypes.MIN_FILE_SIZE_ERROR)
      setError(UploadFile.errorTypes.MIN_FILE_SIZE_ERROR, {
        size: f.size,
        minFileSize: config.minFileSize,
      })
    }

    const maxFileSizeErrorCallback = (f) => {
      setError(UploadFile.errorTypes.MAX_FILE_SIZE_ERROR, {
        size: f.size,
        maxFileSize: config.maxFileSize,
      })
    }

    const res = new Resumable({
      target: config.endpoint,
      chunkSize: config.chunkSize,
      method: 'POST',
      maxFiles: 1,
      fileType: config.accept,
      testChunks: false,
      maxFileSize: config.maxFileSize,
      minFileSize: config.minFileSize,
      simultaneousUploads: 3,
      throttleProgressCallbacks: 1,
      minFileSizeErrorCallback,
      maxFileSizeErrorCallback,
    })

    //
    deleteNode.current && deleteNode.current.addEventListener('click', (event) => {
      event.preventDefault()
      res.cancel()
      onSetFileDelete()
    })

    // const handleDelete = (event) => {
    //   event.preventDefault()
    // }

    //
    res.assignBrowse(browseNode.current)

    //
    res.on('fileAdded', (f, event) => {
      onStateChange(UploadFile.states.IN_PROGRESS)
      handleSetFile(f)
      setProgress(0)
      setFileName(f.fileName)
      res.upload()
    })

    res.on('fileSuccess', (f, event) => {
      let result

      try {
        result = JSON.parse(event)
      } catch (e) {
        result = {}
      }
      setError(null)

      const path = URL.createObjectURL(f.file)

      if (mounted) {
        setFile({...f, id: result.file, path})
        updateValue({...f, id: result.file, path})
        onSetFileSuccess(f, result)
      }
    })

    res.on('fileError', (f, event) => {
      setError(UploadFile.errorTypes.FILE_ERROR, {
        file: f,
        event,
      })
    })

    res.on('fileProgress', (f, event) => {
    })

    res.on('progress', () => {
      setProgress(res.progress())
    })

    res.on('complete', () => {
      onStateChange(UploadFile.states.COMPLETE)
    })

    if (value) {
      setFile(value)
    }

    return () => {
      mounted = false
      if (res.isUploading()) {
        res.cancel()
      }
    }
  }, [])

  return (

    <div className="ntn-form__upload ntn-upload">
      <div className="ntn-upload__layout">
        <div className="ntn-upload__main">
          <div className="ntn-form__label">{wording.label} *</div>
          <div className="ntn-upload__upload">
            <div className="ntn-upload__browse" ref={browseNode}>
              <button type="button" className="ntn-upload__button">Choisir un fichier</button>
              {file && fileName && (
                <div className="ntn-upload__progress">
                  <span className="ntn-upload__progressbar" style={{ width: progress * 100 + '%'}} />
                  <progress max="100" value={progress * 100}></progress>
                </div>
              )}
            </div>
          </div>
          <div className="ntn-upload__notice">{wording.notice}</div>
        </div>
        <div className="ntn-upload__secondary">
          {progress !== 1 ? (
            <div
              className="ntn-upload__description"
              dangerouslySetInnerHTML={{ __html: wording.description }}
            />
          ) : fileName && (
              <div className="ntn-upload__complete">
                {wording.file1} <br />
                <strong>{fileName}</strong> <br />
                {wording.file2}
              </div>
            )}
            <button ref={deleteNode} type="button" className='ntn-upload__delete'>
              {wording.delete}
            </button>
        </div>
      </div>
    </div>
  )
}

UploadFile.defaultValue = null

UploadFile.errorTypes = {
  MIN_FILE_SIZE_ERROR: 'min_file_size_error',
  MAX_FILE_SIZE_ERROR: 'max_file_size_error',
  FILE_ERROR: 'file_error',
}

UploadFile.states = {
  EMPTY: 'empty',
  COMPLETE: 'complete',
  IN_PROGRESS: 'in_progress',
  ERROR: 'error',
}

UploadFile.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
    id: PropTypes.string,
    path: PropTypes.string,
  })]),
  error: PropTypes.string,
  onError: PropTypes.func,
  onChange: PropTypes.func,
  onSetFile: PropTypes.func,
  onStateChange: PropTypes.func,
  onSetFileDelete: PropTypes.func,
  onSetFileSuccess: PropTypes.func,
  config: PropTypes.shape({
    endpoint: PropTypes.string.isRequired,
    chunkSize: PropTypes.number,
    accept: PropTypes.array,
    minFileSize: PropTypes.number,
    maxFileSize: PropTypes.number,
  }),
}

UploadFile.defaultProps = {
  id: null,
  name: null,
  label: null,
  value: null,
  error: null,
  onChange: () => undefined,
  onSetFile: () => undefined,
  onError: () => undefined,
  onStateChange: () => undefined,
  onSetFileDelete: () => undefined,
  onSetFileSuccess: () => undefined,
  config: {
    endpoint: null,
    chunkSize: 1024 * 512,
    accept: ['pdf', 'jpg'],
    minFileSize: 0,
    maxFileSize: Number.MAX_SAFE_INTEGER,
  },
}

export default UploadFile



// WEBPACK FOOTER //
// ./vendor/bbs/crt/src/resources/js/utils/FileUpload.js